/**
 * FriendsList UI Component - Friends sidebar with presence indicators
 */

import { friendsService, presenceService } from '../../social';
import type { FriendWithProfile, FriendRequest, UserPresence, PresenceStatus } from '../../social/types';
import { UserSearch } from './UserSearch';

export class FriendsList {
  private container: HTMLElement;
  private isCollapsed = false;
  private friends: FriendWithProfile[] = [];
  private pendingRequests: FriendRequest[] = [];
  private presenceUnsubscribe: (() => void) | null = null;
  private searchModal: UserSearch | null = null;

  // Callbacks for game actions
  public onInviteToGame: ((friendId: string) => void) | null = null;
  public onJoinGame: ((roomId: string) => void) | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.render();
    this.loadData();
    this.subscribeToPresence();
  }

  /**
   * Load friends and pending requests
   */
  async loadData(): Promise<void> {
    const [friendsResult, requestsResult] = await Promise.all([
      friendsService.getFriends(),
      friendsService.getPendingRequests(),
    ]);

    if (friendsResult.data) {
      this.friends = friendsResult.data;
    }

    if (requestsResult.data) {
      this.pendingRequests = requestsResult.data;
    }

    this.render();
  }

  /**
   * Subscribe to presence updates
   */
  private subscribeToPresence(): void {
    this.presenceUnsubscribe = presenceService.onPresenceChange((presences) => {
      // Update friends with new presence data
      this.friends = this.friends.map((friend) => {
        const presence = presences.get(friend.id);
        if (presence) {
          return { ...friend, presence };
        }
        return friend;
      });

      // Re-sort by presence
      this.sortFriends();
      this.render();
    });
  }

  /**
   * Sort friends by presence status
   */
  private sortFriends(): void {
    const statusOrder: Record<PresenceStatus, number> = {
      online: 0,
      in_game: 1,
      away: 2,
      offline: 3,
    };

    this.friends.sort((a, b) => {
      const aStatus = a.presence?.status || 'offline';
      const bStatus = b.presence?.status || 'offline';
      return statusOrder[aStatus] - statusOrder[bStatus];
    });
  }

  /**
   * Get status indicator class
   */
  private getStatusClass(status: PresenceStatus | undefined): string {
    switch (status) {
      case 'online':
        return 'status-online';
      case 'in_game':
        return 'status-in-game';
      case 'away':
        return 'status-away';
      default:
        return 'status-offline';
    }
  }

  /**
   * Get status text
   */
  private getStatusText(presence: UserPresence | null): string {
    if (!presence) return 'Offline';
    switch (presence.status) {
      case 'online':
        return 'Online';
      case 'in_game':
        return 'In Game';
      case 'away':
        return 'Away';
      default:
        return 'Offline';
    }
  }

  /**
   * Render avatar
   */
  private renderAvatar(friend: FriendWithProfile): string {
    if (friend.avatarUrl) {
      return `<img src="${friend.avatarUrl}" alt="${friend.username}" class="friend-avatar" />`;
    }
    const initial = (friend.displayName || friend.username).charAt(0).toUpperCase();
    return `<div class="friend-avatar-placeholder">${initial}</div>`;
  }

  /**
   * Toggle sidebar collapsed state
   */
  toggleCollapsed(): void {
    this.isCollapsed = !this.isCollapsed;
    this.render();
  }

  /**
   * Handle accept friend request
   */
  private async handleAcceptRequest(friendshipId: string): Promise<void> {
    const { error } = await friendsService.acceptFriendRequest(friendshipId);
    if (!error) {
      await this.loadData();
    }
  }

  /**
   * Handle reject friend request
   */
  private async handleRejectRequest(friendshipId: string): Promise<void> {
    const { error } = await friendsService.rejectFriendRequest(friendshipId);
    if (!error) {
      await this.loadData();
    }
  }

  /**
   * Handle remove friend
   */
  private async handleRemoveFriend(friendshipId: string): Promise<void> {
    if (confirm('Are you sure you want to remove this friend?')) {
      const { error } = await friendsService.removeFriend(friendshipId);
      if (!error) {
        await this.loadData();
      }
    }
  }

  /**
   * Show add friend search modal
   */
  private showAddFriendModal(): void {
    if (!this.searchModal) {
      const modalContainer = document.createElement('div');
      document.body.appendChild(modalContainer);
      this.searchModal = new UserSearch(modalContainer, {
        onClose: () => {
          if (this.searchModal) {
            this.searchModal.destroy();
            this.searchModal = null;
          }
        },
        onFriendAdded: () => {
          this.loadData();
        },
      });
    }
  }

  /**
   * Render the friends list
   */
  render(): void {
    if (this.isCollapsed) {
      this.container.innerHTML = `
        <div class="friends-sidebar collapsed">
          <button class="friends-toggle-btn" title="Show Friends">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            ${this.pendingRequests.length > 0 ? `<span class="friends-badge">${this.pendingRequests.length}</span>` : ''}
          </button>
        </div>
      `;

      this.container.querySelector('.friends-toggle-btn')?.addEventListener('click', () => {
        this.toggleCollapsed();
      });

      return;
    }

    const onlineFriends = this.friends.filter((f) => f.presence?.status === 'online' || f.presence?.status === 'in_game');
    const offlineFriends = this.friends.filter((f) => !f.presence || f.presence.status === 'offline' || f.presence.status === 'away');

    this.container.innerHTML = `
      <div class="friends-sidebar">
        <div class="friends-header">
          <h3 class="friends-title">Friends</h3>
          <div class="friends-header-actions">
            <button class="friends-add-btn" title="Add Friend">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="8.5" cy="7" r="4"/>
                <line x1="20" y1="8" x2="20" y2="14"/>
                <line x1="23" y1="11" x2="17" y2="11"/>
              </svg>
            </button>
            <button class="friends-toggle-btn" title="Hide Friends">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </button>
          </div>
        </div>

        ${this.pendingRequests.length > 0 ? `
          <div class="friends-section">
            <div class="friends-section-header">
              <span class="friends-section-title">Friend Requests</span>
              <span class="friends-section-count">${this.pendingRequests.length}</span>
            </div>
            <div class="friends-list">
              ${this.pendingRequests.map((request) => `
                <div class="friend-card friend-request" data-id="${request.friendship.id}">
                  <div class="friend-info">
                    ${request.sender.avatarUrl 
                      ? `<img src="${request.sender.avatarUrl}" alt="${request.sender.username}" class="friend-avatar" />`
                      : `<div class="friend-avatar-placeholder">${(request.sender.displayName || request.sender.username).charAt(0).toUpperCase()}</div>`
                    }
                    <div class="friend-details">
                      <span class="friend-name">${request.sender.displayName || request.sender.username}</span>
                      <span class="friend-username">@${request.sender.username}</span>
                    </div>
                  </div>
                  <div class="friend-request-actions">
                    <button class="friend-accept-btn" title="Accept">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </button>
                    <button class="friend-reject-btn" title="Reject">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
        ` : ''}

        <div class="friends-section">
          <div class="friends-section-header">
            <span class="friends-section-title">Online</span>
            <span class="friends-section-count">${onlineFriends.length}</span>
          </div>
          <div class="friends-list">
            ${onlineFriends.length === 0 ? `
              <p class="friends-empty">No friends online</p>
            ` : onlineFriends.map((friend) => this.renderFriendCard(friend)).join('')}
          </div>
        </div>

        <div class="friends-section">
          <div class="friends-section-header">
            <span class="friends-section-title">Offline</span>
            <span class="friends-section-count">${offlineFriends.length}</span>
          </div>
          <div class="friends-list">
            ${offlineFriends.length === 0 ? `
              <p class="friends-empty">No offline friends</p>
            ` : offlineFriends.map((friend) => this.renderFriendCard(friend)).join('')}
          </div>
        </div>
      </div>
    `;

    this.attachEventListeners();
  }

  /**
   * Render a single friend card
   */
  private renderFriendCard(friend: FriendWithProfile): string {
    const statusClass = this.getStatusClass(friend.presence?.status);
    const statusText = this.getStatusText(friend.presence);
    const isInGame = friend.presence?.status === 'in_game';
    const isOnline = friend.presence?.status === 'online';

    return `
      <div class="friend-card" data-id="${friend.id}" data-friendship-id="${friend.friendship.id}">
        <div class="friend-info">
          <div class="friend-avatar-wrapper">
            ${this.renderAvatar(friend)}
            <span class="friend-status-dot ${statusClass}"></span>
          </div>
          <div class="friend-details">
            <span class="friend-name">${friend.displayName || friend.username}</span>
            <span class="friend-status-text">${statusText}${isInGame ? ' - Playing' : ''}</span>
          </div>
        </div>
        <div class="friend-actions">
          ${isOnline ? `
            <button class="friend-action-btn invite-btn" title="Invite to Game">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polygon points="5 3 19 12 5 21 5 3"/>
              </svg>
            </button>
          ` : ''}
          ${isInGame && friend.presence?.currentRoomId ? `
            <button class="friend-action-btn join-btn" title="Join Game" data-room="${friend.presence.currentRoomId}">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                <polyline points="10 17 15 12 10 7"/>
                <line x1="15" y1="12" x2="3" y2="12"/>
              </svg>
            </button>
          ` : ''}
          <button class="friend-action-btn more-btn" title="More Options">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="1"/>
              <circle cx="19" cy="12" r="1"/>
              <circle cx="5" cy="12" r="1"/>
            </svg>
          </button>
        </div>
        <div class="friend-dropdown hidden">
          <button class="friend-dropdown-item remove-friend">Remove Friend</button>
        </div>
      </div>
    `;
  }

  /**
   * Attach event listeners
   */
  private attachEventListeners(): void {
    // Toggle button
    this.container.querySelector('.friends-toggle-btn')?.addEventListener('click', () => {
      this.toggleCollapsed();
    });

    // Add friend button
    this.container.querySelector('.friends-add-btn')?.addEventListener('click', () => {
      this.showAddFriendModal();
    });

    // Accept request buttons
    this.container.querySelectorAll('.friend-accept-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const card = (e.target as HTMLElement).closest('.friend-card');
        const friendshipId = card?.getAttribute('data-id');
        if (friendshipId) {
          this.handleAcceptRequest(friendshipId);
        }
      });
    });

    // Reject request buttons
    this.container.querySelectorAll('.friend-reject-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const card = (e.target as HTMLElement).closest('.friend-card');
        const friendshipId = card?.getAttribute('data-id');
        if (friendshipId) {
          this.handleRejectRequest(friendshipId);
        }
      });
    });

    // Invite to game buttons
    this.container.querySelectorAll('.invite-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const card = (e.target as HTMLElement).closest('.friend-card');
        const friendId = card?.getAttribute('data-id');
        if (friendId && this.onInviteToGame) {
          this.onInviteToGame(friendId);
        }
      });
    });

    // Join game buttons
    this.container.querySelectorAll('.join-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const roomId = (e.target as HTMLElement).closest('.join-btn')?.getAttribute('data-room');
        if (roomId && this.onJoinGame) {
          this.onJoinGame(roomId);
        }
      });
    });

    // More options buttons
    this.container.querySelectorAll('.more-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const card = (e.target as HTMLElement).closest('.friend-card');
        const dropdown = card?.querySelector('.friend-dropdown');
        dropdown?.classList.toggle('hidden');
      });
    });

    // Remove friend buttons
    this.container.querySelectorAll('.remove-friend').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const card = (e.target as HTMLElement).closest('.friend-card');
        const friendshipId = card?.getAttribute('data-friendship-id');
        if (friendshipId) {
          this.handleRemoveFriend(friendshipId);
        }
      });
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', () => {
      this.container.querySelectorAll('.friend-dropdown').forEach((dropdown) => {
        dropdown.classList.add('hidden');
      });
    });
  }

  /**
   * Cleanup
   */
  destroy(): void {
    if (this.presenceUnsubscribe) {
      this.presenceUnsubscribe();
    }
    if (this.searchModal) {
      this.searchModal.destroy();
    }
    this.container.innerHTML = '';
  }
}
